import React from "react";
import { Form, Row, Col, Spinner } from "react-bootstrap";
import defaultCompany from "../../images/default-company.svg";
import { US_STATES } from "../../constants/global-constants";

const CompanyInfo = ({
  formData,
  handleInputChange,
  handleImageUpload,
  handleImageClick,
  profile,
  validationErrors = {},
  isImageUploading,
  uploadedImageUrl, // URL for company logo
}) => {
  // Handle both uploaded image URL and existing company logo
  const displayImageUrl =
    uploadedImageUrl ||
    profile?.company?.profile_image?.presigned_url ||
    defaultCompany;

  return (
    <>
      <h2 className="mb-3">Company Information</h2>
      <Row className="g-3">
        <Col xs={12} className="text-center">
          <Form.Group className="mb-3 company-logo-container">
            <div className="profile-image-wrapper">
              <button
                type="button"
                onClick={handleImageClick("company_logo")}
                className="user-profile-button"
                disabled={isImageUploading}
              >
                <img
                  src={displayImageUrl}
                  alt="Company Logo"
                  className="user-profile-icon"
                  style={{ width: "120px", height: "120px" }}
                />
                {isImageUploading && (
                  <div className="upload-overlay">
                    <Spinner animation="border" size="sm" variant="light" />
                  </div>
                )}
              </button>
            </div>
            <Form.Control
              type="file"
              id="company_logo-input"
              className="d-none"
              accept="image/*"
              onChange={handleImageUpload("company_logo")}
              isInvalid={!!validationErrors.company_logo}
            />
            <Form.Label
              htmlFor="company_logo-input"
              className="btn btn-outline-primary"
              style={{ fontWeight: "500" }}
            >
              Upload image
            </Form.Label>
            {validationErrors.company_logo && (
              <Form.Control.Feedback
                type="invalid"
                style={{ display: "block" }}
              >
                {validationErrors.company_logo}
              </Form.Control.Feedback>
            )}
          </Form.Group>
        </Col>

        <Col xs={12}>
          <Form.Group
            className={validationErrors.company_name ? "has-error" : ""}
          >
            <Form.Label className="required-field">Company Name*</Form.Label>
            <Form.Control
              type="text"
              name="company_name"
              value={formData.company_name}
              onChange={handleInputChange}
              required
              isInvalid={!!validationErrors.company_name}
              className={validationErrors.company_name ? "error-shake" : ""}
            />
            <Form.Control.Feedback type="invalid">
              {validationErrors.company_name}
            </Form.Control.Feedback>
          </Form.Group>
        </Col>

        <Col xs={12}>
          <Form.Group>
            <Form.Label>Company Address Line 1</Form.Label>
            <Form.Control
              type="text"
              name="company_address.address_line1"
              value={formData.company_address.address_line1}
              onChange={handleInputChange}
              isInvalid={!!validationErrors["company_address.address_line1"]}
            />
            <Form.Control.Feedback type="invalid">
              {validationErrors["company_address.address_line1"]}
            </Form.Control.Feedback>
          </Form.Group>
        </Col>

        <Col xs={12}>
          <Form.Group>
            <Form.Label>Company Address Line 2</Form.Label>
            <Form.Control
              type="text"
              name="company_address.address_line2"
              value={formData.company_address.address_line2}
              onChange={handleInputChange}
            />
          </Form.Group>
        </Col>

        <Col md={6}>
          <Form.Group>
            <Form.Label>Company City</Form.Label>
            <Form.Control
              type="text"
              name="company_address.city"
              value={formData.company_address.city}
              onChange={handleInputChange}
              isInvalid={!!validationErrors["company_address.city"]}
            />
            <Form.Control.Feedback type="invalid">
              {validationErrors["company_address.city"]}
            </Form.Control.Feedback>
          </Form.Group>
        </Col>

        <Col md={6}>
          <Form.Group>
            <Form.Label>Company State</Form.Label>
            <Form.Select
              name="company_address.state"
              value={formData.company_address.state}
              onChange={handleInputChange}
              isInvalid={!!validationErrors["company_address.state"]}
            >
              <option value="">Select State</option>
              {US_STATES.map((state) => (
                <option key={state.value} value={state.value}>
                  {state.label}
                </option>
              ))}
            </Form.Select>
            <Form.Control.Feedback type="invalid">
              {validationErrors["company_address.state"]}
            </Form.Control.Feedback>
          </Form.Group>
        </Col>

        <Col md={6}>
          <Form.Group>
            <Form.Label>Postal Code</Form.Label>
            <Form.Control
              type="text"
              name="company_address.zip_code"
              value={formData.company_address.zip_code}
              onChange={handleInputChange}
              isInvalid={!!validationErrors["company_address.zip_code"]}
            />
            <Form.Control.Feedback type="invalid">
              {validationErrors["company_address.zip_code"]}
            </Form.Control.Feedback>
          </Form.Group>
        </Col>

        <Col md={6}>
          <Form.Group
            className={validationErrors.company_phone ? "has-error" : ""}
          >
            <Form.Label className="required-field">Phone*</Form.Label>
            <Form.Control
              type="tel"
              name="company_phone"
              value={formData.company_phone}
              onChange={handleInputChange}
              required
              placeholder="(123) 456-7890"
              isInvalid={!!validationErrors.company_phone}
              className={validationErrors.company_phone ? "error-shake" : ""}
            />
            <Form.Control.Feedback type="invalid">
              {validationErrors.company_phone}
            </Form.Control.Feedback>
            <Form.Text className="text-muted">
              Format: (123) 456-7890 or 123-456-7890
            </Form.Text>
          </Form.Group>
        </Col>

        <Col xs={12}>
          <Form.Group>
            <Form.Label>Email</Form.Label>
            <Form.Control
              type="email"
              name="company_email"
              value={formData.company_email}
              onChange={handleInputChange}
              isInvalid={!!validationErrors.company_email}
            />
            <Form.Control.Feedback type="invalid">
              {validationErrors.company_email}
            </Form.Control.Feedback>
          </Form.Group>
        </Col>
      </Row>
    </>
  );
};

export default CompanyInfo;
